import { DateString, getNumberString, NumberString, PriceString, ProfitString, } from '@fourthwall/components';
import { AnalyticsBar, AnalyticsCountry, createChartStatsTableColumnsTotalFreeMembers, createChartStatsTableColumnsTotalMembers, createChartStatsTableTotalFreeMembers, createChartStatsTableTotalMembers, createTopProductsByUnitsSoldData, getDateFormat, getPaymentMethodLabel, SUMMARY_ROW_ID, } from '@fourthwall/module-analytics';
import { getPrice } from '@fourthwall/utils';
import { lowerCase, startCase } from 'lodash-es';
import React from 'react';
import { OfferLink } from '../../../../../components/OfferLink/OfferLink';
import { REPORT_TYPES } from '../../../../../consts';
import { getSocialSourceLabel } from '../../../utils/getSocialSourceLabel';
import { mapTopTiersToAnalyticsRows } from '../../../utils/mapTopTiersToAnalyticsRows';
import { DataItemValue } from '../components/DataItemValue/DataItemValue';
import { createChartStatsTableColumns } from './createChartStatsTableColumns';
import { createChartStatsTableColumnsMembersChange } from './createChartStatsTableColumnsMembersChange';
import { createChartStatsTableConversionRates } from './createChartStatsTableConversionRates';
import { createChartStatsTableData } from './createChartStatsTableData';
import { createChartStatsTableFreeMembersChange } from './createChartStatsTableFreeMembersChange';
import { createChartStatsTableMembersChange } from './createChartStatsTableMembersChange';
import { createChartStatsTableProfit } from './createChartStatsTableProfit';
import { createChartStatsTableUniqueVisitors } from './createChartStatsTableUniqueVisitors';
export const createStatsTable = (reportType, params, analyticsChartQuery, membershipsChartQuery, topOfferFullTableQuery, saleByCountryFullTableQuery, membershipsTopTiersTableQuery, membershipAccountQuery, paymentMethodUsageTableQuery, shopsStatsTrafficQuery) => {
    if (reportType === REPORT_TYPES.profit.slug) {
        return {
            columns: [
                {
                    header: params.precision === 'HOUR' ? 'Time' : 'Day',
                    render: (item) => {
                        if (item.id === SUMMARY_ROW_ID)
                            return item.day;
                        return React.createElement(DateString, { date: item.day, format: getDateFormat(params.precision), utc: true });
                    },
                },
                {
                    header: 'Gross Sales',
                    tooltip: 'This includes the total amount of money spent across all of your Fourthwall transactions (excluding any discounts)',
                    render: (item) => React.createElement(PriceString, { value: item.grossSales }),
                },
                {
                    header: 'Discounts',
                    tooltip: 'This includes discounts from your promotional campaigns',
                    render: (item) => React.createElement(PriceString, { value: item.discounts }),
                },
                {
                    header: 'Costs',
                    tooltip: 'This includes all product costs, shipping costs, taxes, fulfillment costs, digital product fees, membership fees, and payment fees',
                    render: (item) => React.createElement(PriceString, { value: item.cost }),
                },
                {
                    header: 'Cancels',
                    tooltip: 'This includes all canceled transactions',
                    render: (item) => React.createElement(PriceString, { value: item.cancellations }),
                },
                {
                    header: 'Refunds',
                    tooltip: 'This includes any transaction that was refunded by you but was not canceled',
                    render: (item) => React.createElement(PriceString, { value: item.refunds }),
                },
                {
                    header: 'Profit',
                    render: (item) => React.createElement(ProfitString, { value: item.profit }),
                },
            ],
            data: createChartStatsTableProfit(analyticsChartQuery.data?.current, params),
            emptyMessage: 'No contributions found in this date range.',
            isLoading: analyticsChartQuery.isLoading,
            getRowBold: ({ id }) => id === SUMMARY_ROW_ID,
        };
    }
    if (reportType === REPORT_TYPES['memberships-total-members'].slug) {
        return {
            columns: createChartStatsTableColumnsTotalMembers(params),
            data: createChartStatsTableTotalMembers(membershipsChartQuery.data?.current, params),
            emptyMessage: 'No data found in this date range.',
            isLoading: membershipsChartQuery.isLoading,
        };
    }
    if (reportType === REPORT_TYPES['memberships-total-free-members'].slug) {
        return {
            columns: createChartStatsTableColumnsTotalFreeMembers(params),
            data: createChartStatsTableTotalFreeMembers(membershipsChartQuery.data?.current, params),
            emptyMessage: 'No data found in this date range.',
            isLoading: membershipsChartQuery.isLoading,
        };
    }
    if (reportType === REPORT_TYPES['memberships-members-change'].slug) {
        return {
            columns: createChartStatsTableColumnsMembersChange(params),
            data: createChartStatsTableMembersChange(membershipsChartQuery.data?.current, params),
            emptyMessage: 'No data found in this date range.',
            isLoading: membershipsChartQuery.isLoading,
        };
    }
    if (reportType === REPORT_TYPES['memberships-free-members-change'].slug) {
        return {
            columns: createChartStatsTableColumnsMembersChange(params),
            data: createChartStatsTableFreeMembersChange(membershipsChartQuery.data?.current, params),
            emptyMessage: 'No data found in this date range.',
            isLoading: membershipsChartQuery.isLoading,
        };
    }
    if (reportType === 'orders') {
        return {
            columns: [
                {
                    header: params.precision === 'HOUR' ? 'Time' : 'Day',
                    render: (item) => {
                        if (item.id === SUMMARY_ROW_ID)
                            return item.day;
                        return React.createElement(DateString, { date: item.day, format: getDateFormat(params.precision), utc: true });
                    },
                },
                {
                    header: 'Shop Orders',
                    render: (item) => item.contributionOrders,
                },
                {
                    header: 'Donations',
                    render: (item) => item.contributionDonations,
                },
                {
                    header: 'Twitch Gifts',
                    render: (item) => item.contributionGiveaway,
                },
                {
                    header: 'Giveaway Links',
                    render: (item) => item.contributionGiftsOnCreatorCost,
                },
                {
                    header: 'Memberships',
                    render: (item) => item.contributionSubscriptions,
                },
                {
                    header: 'Total',
                    render: (item) => item.contribution,
                },
            ],
            data: createChartStatsTableData(analyticsChartQuery.data?.current, params, [
                'contributionOrders',
                'contributionDonations',
                'contributionGiveaway',
                'contributionGiftsOnCreatorCost',
                'contributionSubscriptions',
                'contribution',
            ]),
            emptyMessage: 'No contributions found in this date range.',
            isLoading: analyticsChartQuery.isLoading,
            getRowBold: ({ id }) => id === SUMMARY_ROW_ID,
        };
    }
    if (reportType === REPORT_TYPES['average-order-value'].slug) {
        return {
            columns: [
                {
                    header: params.precision === 'HOUR' ? 'Time' : 'Day',
                    render: (item) => {
                        if (item.id === SUMMARY_ROW_ID)
                            return item.day;
                        return React.createElement(DateString, { date: item.day, format: getDateFormat(params.precision), utc: true });
                    },
                },
                {
                    header: 'Average Sales Value',
                    render: (item) => React.createElement(PriceString, { value: item.averageSales }),
                },
                {
                    header: 'Average Profit',
                    render: (item) => React.createElement(PriceString, { value: item.averageProfit }),
                },
            ],
            data: createChartStatsTableData(analyticsChartQuery.data?.current, params, [
                'averageSales',
                'averageProfit',
            ]),
            emptyMessage: 'No contributions found in this date range.',
            isLoading: analyticsChartQuery.isLoading,
            getRowBold: ({ id }) => id === SUMMARY_ROW_ID,
        };
    }
    if (reportType === REPORT_TYPES['top-products-by-units-sold'].slug) {
        return {
            columns: [
                {
                    header: 'Product',
                    render: (item) => (React.createElement(OfferLink, { name: item.name, label: REPORT_TYPES['top-products-by-units-sold'].title, id: item.offerId, status: item.status })),
                },
                { header: 'Units sold', render: (item) => React.createElement(NumberString, { value: item.unitsSold }) },
            ],
            data: createTopProductsByUnitsSoldData(topOfferFullTableQuery.data?.current.data),
            emptyMessage: 'No contributions found in this date range.',
            isLoading: topOfferFullTableQuery.isLoading,
        };
    }
    if (reportType === REPORT_TYPES['sales-by-country'].slug) {
        return {
            columns: [
                { header: 'Country', render: (item) => React.createElement(AnalyticsCountry, { countryCode: item.country }) },
                { header: 'Contribution', render: (item) => React.createElement(NumberString, { value: item.contribution }) },
                { header: 'Income', render: (item) => React.createElement(ProfitString, { value: item.income }) },
            ],
            data: saleByCountryFullTableQuery.data?.current.data || [],
            emptyMessage: 'No contributions found in this date range.',
            isLoading: saleByCountryFullTableQuery.isLoading,
        };
    }
    if (reportType === REPORT_TYPES['membership-top-tiers'].slug) {
        return {
            columns: [
                { header: 'Tier', render: (item) => item.label },
                { header: 'Supporters', render: (item) => React.createElement(NumberString, { value: item.value }) },
            ],
            data: mapTopTiersToAnalyticsRows(membershipsTopTiersTableQuery.data?.current.data, membershipAccountQuery.data?.tiers) || [],
            emptyMessage: 'No data found in this date range.',
            isLoading: membershipsTopTiersTableQuery.isLoading || membershipAccountQuery.isLoading,
        };
    }
    if (reportType === REPORT_TYPES['payment-method-used'].slug) {
        const totalValue = paymentMethodUsageTableQuery.data?.current.totalValue || 100;
        return {
            columns: [
                {
                    header: 'Payment method',
                    render: getPaymentMethodLabel,
                },
                {
                    header: 'Percentage',
                    width: '70%',
                    align: 'left',
                    render: (item) => (React.createElement(AnalyticsBar, { value: item.percentage, label: `${item.percentage}%`, maxValue: totalValue })),
                },
            ],
            data: paymentMethodUsageTableQuery.data?.current.data || [],
            totalValue,
            emptyMessage: 'No data found in this date range.',
            isLoading: paymentMethodUsageTableQuery.isFetching,
        };
    }
    if (reportType === REPORT_TYPES['conversion-rates'].slug) {
        return {
            columns: [
                {
                    header: 'Day',
                    render: (item) => {
                        if (item.id === SUMMARY_ROW_ID)
                            return item.day;
                        return React.createElement(DateString, { date: item.day, format: getDateFormat(params.precision), utc: true });
                    },
                },
                {
                    header: 'Sessions',
                    render: (item) => React.createElement(DataItemValue, { value: item.totalSessions }),
                },
                {
                    header: 'Added to cart',
                    render: (item) => (React.createElement(DataItemValue, { value: item.cartCreated.value, subValue: `${item.cartCreated.percentage}%` })),
                },
                {
                    header: 'Checkout started',
                    render: (item) => (React.createElement(DataItemValue, { value: item.checkoutStarted.value, subValue: `${item.checkoutStarted.percentage}%` })),
                },
                {
                    header: 'Successful checkout',
                    render: (item) => (React.createElement(DataItemValue, { value: item.checkoutPaid.value, subValue: `${item.checkoutPaid.percentage}%` })),
                },
                { header: `Conversion rate`, render: (item) => `${item.conversionRate}%` },
            ],
            getRowBold: ({ id }) => id === SUMMARY_ROW_ID,
            data: createChartStatsTableConversionRates(shopsStatsTrafficQuery.data?.current.conversionRates),
            emptyMessage: 'No data found in this date range.',
            isLoading: shopsStatsTrafficQuery.isFetching,
        };
    }
    if (reportType === REPORT_TYPES['unique-visitors'].slug) {
        return {
            columns: [
                {
                    header: 'Day',
                    render: (item) => {
                        if (item.id === SUMMARY_ROW_ID)
                            return item.day;
                        return React.createElement(DateString, { date: item.day, format: getDateFormat(params.precision), utc: true });
                    },
                },
                { header: `Visitors`, render: (item) => item.visitors },
            ],
            data: createChartStatsTableUniqueVisitors(shopsStatsTrafficQuery.data?.current.uniqueVisitors),
            getRowBold: ({ id }) => id === SUMMARY_ROW_ID,
            emptyMessage: 'No data found in this date range.',
            isLoading: shopsStatsTrafficQuery.isFetching,
        };
    }
    if (reportType === REPORT_TYPES['sales-by-traffic-source'].slug) {
        const totalValue = shopsStatsTrafficQuery.data?.current.salesByTrafficSourceTable?.totalValue || 100;
        return {
            columns: [
                {
                    header: 'Traffic source',
                    render: (item) => startCase(lowerCase(item.source)),
                },
                {
                    header: 'Sale value',
                    width: '70%',
                    align: 'left',
                    render: (item) => (React.createElement(AnalyticsBar, { label: getPrice(item.income, {
                            currency: shopsStatsTrafficQuery.data?.current.salesByTrafficSourceTable?.types?.currency,
                        }), value: item.income, maxValue: totalValue })),
                },
            ],
            data: shopsStatsTrafficQuery.data?.current.salesByTrafficSourceTable?.data || [],
            totalValue,
            emptyMessage: 'No data found in this date range.',
            isLoading: shopsStatsTrafficQuery.isFetching,
        };
    }
    if (reportType === REPORT_TYPES['sessions-by-traffic-source'].slug) {
        const totalValue = shopsStatsTrafficQuery.data?.current.sessionsByTrafficSource?.totalValue || 100;
        return {
            columns: [
                {
                    header: 'Traffic source',
                    render: (item) => startCase(lowerCase(item.source)),
                },
                {
                    header: 'Sessions',
                    width: '70%',
                    align: 'left',
                    render: (item) => (React.createElement(AnalyticsBar, { value: item.count, label: getNumberString(item.count), maxValue: totalValue })),
                },
            ],
            data: shopsStatsTrafficQuery.data?.current.sessionsByTrafficSource?.data || [],
            totalValue,
            emptyMessage: 'No data found in this date range.',
            isLoading: shopsStatsTrafficQuery.isFetching,
        };
    }
    if (reportType === REPORT_TYPES['sales-by-social-source'].slug) {
        const totalValue = shopsStatsTrafficQuery.data?.current.salesBySocialSourceTable?.totalValue || 100;
        return {
            columns: [
                {
                    header: 'Social source',
                    render: getSocialSourceLabel,
                },
                {
                    header: 'Sale value',
                    width: '70%',
                    align: 'left',
                    render: (item) => (React.createElement(AnalyticsBar, { value: item.income, label: getPrice(item.income, {
                            currency: shopsStatsTrafficQuery.data?.current.salesBySocialSourceTable?.types?.currency,
                        }), maxValue: totalValue })),
                },
            ],
            data: shopsStatsTrafficQuery.data?.current.salesBySocialSourceTable?.data || [],
            totalValue,
            emptyMessage: 'No data found in this date range.',
            isLoading: shopsStatsTrafficQuery.isFetching,
        };
    }
    if (reportType === REPORT_TYPES['sessions-by-social-source'].slug) {
        const totalValue = shopsStatsTrafficQuery.data?.current.sessionsBySocialSource?.totalValue || 100;
        return {
            columns: [
                {
                    header: 'Social source',
                    render: getSocialSourceLabel,
                },
                {
                    header: 'Sessions',
                    width: '70%',
                    align: 'left',
                    render: (item) => (React.createElement(AnalyticsBar, { value: item.count, label: getNumberString(item.count), maxValue: totalValue })),
                },
            ],
            data: shopsStatsTrafficQuery.data?.current.sessionsBySocialSource?.data || [],
            totalValue,
            emptyMessage: 'No data found in this date range.',
            isLoading: shopsStatsTrafficQuery.isFetching,
        };
    }
    // this should never happen
    return {
        columns: createChartStatsTableColumns(params),
        data: createChartStatsTableData(analyticsChartQuery.data?.current, params, []),
        emptyMessage: 'No contributions found in this date range.',
        isLoading: analyticsChartQuery.isLoading,
        getRowBold: ({ id }) => id === SUMMARY_ROW_ID,
    };
};
